import { useCallback, useEffect, useRef } from 'react';

const DEFAULT_SCROLL_OPTIONS = {
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest',
};

const scrollElementIntoView = (element, options = {}) => {
    element?.scrollIntoView({ ...DEFAULT_SCROLL_OPTIONS, ...options });
};

/**
 * Scroll an element into view when `when` becomes truthy.
 *
 * @example
 * const panelRef = useScrollIntoView(isOpen);
 * return isOpen ? <div ref={panelRef}>...</div> : null;
 */
const useScrollIntoView = (when, options = {}) => {
    const ref = useRef(null);
    const { behavior, block, inline } = { ...DEFAULT_SCROLL_OPTIONS, ...options };

    useEffect(() => {
        if (!when) {
            return;
        }
        scrollElementIntoView(ref.current, { behavior, block, inline });
    }, [when, behavior, block, inline]);

    return ref;
};

/**
 * Scroll a list item into view after `items` updates. Call requestScroll before updating state.
 *
 * @example
 * const { getRef, requestScroll } = useScrollToIndex(items);
 *
 * const onAdd = () => {
 *     requestScroll(items.length);
 *     setItems([...items, newItem]);
 * };
 *
 * return items.map((item, i) => <div key={item.id} ref={getRef(i)}>...</div>);
 */
export const useScrollToIndex = (items, options = {}) => {
    const itemRefs = useRef([]);
    const scrollToIndexRef = useRef(null);
    const { behavior, block, inline } = { ...DEFAULT_SCROLL_OPTIONS, ...options };

    useEffect(() => {
        const index = scrollToIndexRef.current;
        if (index == null) {
            return;
        }
        scrollToIndexRef.current = null;
        scrollElementIntoView(itemRefs.current[index], { behavior, block, inline });
    }, [items, behavior, block, inline]);

    const getRef = useCallback((index) => (el) => {
        itemRefs.current[index] = el;
    }, []);

    const requestScroll = useCallback((index) => {
        scrollToIndexRef.current = index;
    }, []);

    return { getRef, requestScroll };
};

export default useScrollIntoView;
